import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as CsvIcon,
  IosShare as ExportIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ExportButton = ({ data, columns, filename = 'export' }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Prepare data for the table
    const tableData = data.map(row => 
      columns.map(col => {
        const value = row[col.field];
        // Format numbers to 2 decimal places if they are numbers
        return typeof value === 'number' ? value.toFixed(2) : (value || '');
      })
    );

    // Add the table to the PDF
    autoTable(doc, {
      head: [columns.map(col => col.headerName)],
      body: tableData,
      startY: 20,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
      },
    });

    // Save the PDF
    doc.save(`${filename}.pdf`);
    handleClose();
  };

  const exportToCSV = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      data.map(row => {
        const newRow = {};
        columns.forEach(col => {
          newRow[col.headerName] = row[col.field] || '';
        });
        return newRow;
      })
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = XLSX.write(workbook, { bookType: 'csv', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'text/csv;charset=utf-8' });
    saveAs(dataBlob, `${filename}.csv`);
    handleClose();
  };

  const exportToDOC = () => {
    // For DOC export, we'll create a simple HTML table and convert it
    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const tbody = document.createElement('tbody');
    
    // Add headers
    const headerRow = document.createElement('tr');
    columns.forEach(col => {
      const th = document.createElement('th');
      th.textContent = col.headerName;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);
    
    // Add data rows
    data.forEach(row => {
      const tr = document.createElement('tr');
      columns.forEach(col => {
        const td = document.createElement('td');
        const value = row[col.field];
        td.textContent = typeof value === 'number' ? value.toFixed(2) : (value || '');
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    
    // Convert to DOC
    const html = table.outerHTML;
    const blob = new Blob([html], { type: 'application/msword' });
    saveAs(blob, `${filename}.doc`);
    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ExportIcon />}
        onClick={handleClick}
        size="small"
        sx={{ 
          borderRadius: '4px',
          textTransform: 'none',
          py: 0.5,
          px: 1,
        }}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: '8px',
            '& .MuiMenuItem-root': {
              py: 1,
              px: 2,
            },
          },
        }}
      >
        <MenuItem 
          onClick={exportToPDF}
          sx={{
            '&:hover': {
              backgroundColor: 'primary.light',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            },
          }}
        >
          <ListItemIcon>
            <PdfIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2" fontWeight="medium">
              Export as PDF
            </Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={exportToCSV}
          sx={{
            '&:hover': {
              backgroundColor: 'primary.light',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            },
          }}
        >
          <ListItemIcon>
            <CsvIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2" fontWeight="medium">
              Export as CSV
            </Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={exportToDOC}
          sx={{
            '&:hover': {
              backgroundColor: 'primary.light',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            },
          }}
        >
          <ListItemIcon>
            <DocIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2" fontWeight="medium">
              Export as DOC
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ExportButton; 